import { Outlet, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ArrowUpRight, SignOut, User as UserIcon, List, X, Gear } from "@phosphor-icons/react";
import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import OrdeneliMark from "../OrdeneliMark";

const adminEmails = () =>
  (process.env.REACT_APP_ADMIN_EMAILS || "kirovd555@gmail.com")
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

const isPlatformAdmin = (user) =>
  !!user?.email && adminEmails().includes(String(user.email).toLowerCase());

const navLink = ({ isActive }) =>
  `text-sm font-semibold transition-colors ${
    isActive ? "text-[#0A0A0A]" : "text-[#525252] hover:text-[#0A0A0A]"
  }`;

export default function MainLayout() {
  const { user, usage, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const capPct = usage?.cap ? Math.min(100, Math.round((usage.used / usage.cap) * 100)) : 0;
  const admin = isPlatformAdmin(user);

  // Close menus on route change
  useEffect(() => {
    setMenuOpen(false);
    setMobileNavOpen(false);
  }, [location.pathname]);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="fixed top-0 w-full z-50 bg-white/85 backdrop-blur-xl border-b border-[#E5E7EB] print:hidden">
        <div className="container-wide h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <OrdeneliMark size={28} />
            <span className="font-display text-base sm:text-lg font-extrabold tracking-tight">
              ORDENELI<span className="text-[#002FA7]">.</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            <NavLink to="/" end className={navLink} data-testid="nav-home">Home</NavLink>
            <NavLink to="/dashboard" className={navLink} data-testid="nav-dashboard">Tools</NavLink>
            {user && <NavLink to="/workflow" className={navLink} data-testid="nav-workflow">Workflow</NavLink>}
            {user && <NavLink to="/strategy" className={navLink} data-testid="nav-strategy">Strategy</NavLink>}
            {admin && <NavLink to="/admin" className={navLink} data-testid="nav-admin">Admin</NavLink>}
            <NavLink to="/pricing" className={navLink} data-testid="nav-pricing">Pricing</NavLink>
            {user && <NavLink to="/history" className={navLink} data-testid="nav-history">History</NavLink>}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            {loading ? (
              <div className="h-8 w-16 sm:w-24 bg-[#F5F5F5] animate-pulse" />
            ) : user ? (
              <div className="relative">
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  className="flex items-center gap-2 text-sm font-semibold"
                  data-testid="user-menu-btn"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-8 w-8 object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="h-8 w-8 bg-[#0A0A0A] text-white flex items-center justify-center">
                      <UserIcon size={14} weight="bold" />
                    </div>
                  )}
                  <span className="hidden md:inline max-w-[140px] truncate">{user.name}</span>
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 border border-[#E5E7EB] bg-white py-1 min-w-[240px] shadow-lg z-50">
                    <div className="px-4 py-3 border-b border-[#E5E7EB]">
                      <p className="text-sm font-semibold truncate">{user.name}</p>
                      <p className="text-xs text-[#737373] truncate">{user.email}</p>
                      {usage && (
                        <div className="mt-3" data-testid="menu-usage">
                          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-[#737373]">
                            <span className="capitalize">{usage.plan} plan</span>
                            <span>{usage.used}{usage.cap !== null ? ` / ${usage.cap}` : ""}</span>
                          </div>
                          {usage.cap !== null && (
                            <div className="mt-1.5 h-1 bg-[#F5F5F5] overflow-hidden">
                              <div className={`h-full ${capPct >= 100 ? "bg-[#DC2626]" : "bg-[#002FA7]"}`} style={{ width: `${capPct}%` }} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <Link to="/dashboard" className="block px-4 py-2 text-sm hover:bg-[#F5F5F5] md:hidden">Tools</Link>
                    <Link to="/workflow" className="block px-4 py-2 text-sm hover:bg-[#F5F5F5]">Workflow</Link>
                    <Link to="/strategy" className="block px-4 py-2 text-sm hover:bg-[#F5F5F5]">Strategy</Link>
                    {admin && <Link to="/admin" className="block px-4 py-2 text-sm hover:bg-[#F5F5F5]">Admin</Link>}
                    <Link to="/pricing" className="block px-4 py-2 text-sm hover:bg-[#F5F5F5] md:hidden">Pricing</Link>
                    <Link to="/history" className="block px-4 py-2 text-sm hover:bg-[#F5F5F5]">History</Link>
                    <Link to="/settings" className="block px-4 py-2 text-sm hover:bg-[#F5F5F5] inline-flex items-center gap-2 w-full" data-testid="nav-settings">
                      <Gear size={14} weight="bold" /> Settings
                    </Link>
                    <button
                      onClick={onLogout}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F5F5] inline-flex items-center gap-2"
                      data-testid="logout-btn"
                    >
                      <SignOut size={14} weight="bold" /> Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="text-sm font-semibold text-[#525252] hover:text-[#0A0A0A] hidden md:inline" data-testid="nav-login">
                  Sign in
                </Link>
                <Link to="/login" className="btn-primary text-xs sm:text-sm !px-3 sm:!px-6" data-testid="header-cta">
                  <span className="hidden xs:inline sm:inline">Get started</span>
                  <span className="xs:hidden sm:hidden">Start</span>
                  <ArrowUpRight size={16} weight="bold" />
                </Link>
              </>
            )}

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileNavOpen((v) => !v)}
              className="md:hidden p-1 -mr-1"
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {mobileNavOpen ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
            </button>
          </div>
        </div>

        {mobileNavOpen && (
          <div className="md:hidden border-t border-[#E5E7EB] bg-white" data-testid="mobile-menu">
            <nav className="flex flex-col">
              <NavLink to="/" end className="px-6 py-3 border-b border-[#E5E7EB] text-base font-semibold">Home</NavLink>
              <NavLink to="/dashboard" className="px-6 py-3 border-b border-[#E5E7EB] text-base font-semibold">Tools</NavLink>
              {user && <NavLink to="/workflow" className="px-6 py-3 border-b border-[#E5E7EB] text-base font-semibold">Workflow</NavLink>}
              {user && <NavLink to="/strategy" className="px-6 py-3 border-b border-[#E5E7EB] text-base font-semibold">Strategy</NavLink>}
              {admin && <NavLink to="/admin" className="px-6 py-3 border-b border-[#E5E7EB] text-base font-semibold">Admin</NavLink>}
              <NavLink to="/pricing" className="px-6 py-3 border-b border-[#E5E7EB] text-base font-semibold">Pricing</NavLink>
              {user && <NavLink to="/history" className="px-6 py-3 border-b border-[#E5E7EB] text-base font-semibold">History</NavLink>}
              {!user && <NavLink to="/login" className="px-6 py-3 text-base font-semibold text-[#002FA7]">Sign in</NavLink>}
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1 pt-16">
        <Outlet />
      </main>

      <footer className="border-t border-[#E5E7EB] mt-16 md:mt-20 print:hidden">
        <div className="container-wide py-10 md:py-14 grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-10">
          <div className="col-span-2">
            <div className="flex items-center gap-2">
              <OrdeneliMark size={28} />
              <span className="font-display text-lg font-extrabold">ORDENELI.</span>
            </div>
            <p className="mt-4 text-[#525252] max-w-sm text-sm md:text-base">
              The operating system for modern marketing teams. Ten AI tools, one studio.
            </p>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373] mb-4">Product</p>
            <ul className="space-y-2 text-sm text-[#525252]">
              <li><Link to="/dashboard">All Tools</Link></li>
              {user && <li><Link to="/workflow">Workflow</Link></li>}
              <li><Link to="/pricing">Pricing</Link></li>
              {user && <li><Link to="/history">History</Link></li>}
            </ul>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373] mb-4">Developers</p>
            <ul className="space-y-2 text-sm text-[#525252]">
              <li><Link to="/docs/api" data-testid="footer-api-docs-link" className="hover:text-[#0A0A0A]">API Docs</Link></li>
              <li><Link to="/docs/workflow" data-testid="footer-workflow-docs-link" className="hover:text-[#0A0A0A]">Workflow Guide</Link></li>
              {user && <li><Link to="/settings?tab=api" className="hover:text-[#0A0A0A]">API Keys</Link></li>}
            </ul>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373] mb-4">Legal</p>
            <ul className="space-y-2 text-sm text-[#525252]">
              <li><Link to="/privacy" className="hover:text-[#0A0A0A]" data-testid="footer-privacy-link">Privacy</Link></li>
              <li><Link to="/terms" className="hover:text-[#0A0A0A]" data-testid="footer-terms-link">Terms</Link></li>
              <li><Link to="/dpa" className="hover:text-[#0A0A0A]" data-testid="footer-dpa-link">DPA</Link></li>
              <li><Link to="/cookies" className="hover:text-[#0A0A0A]" data-testid="footer-cookies-link">Cookies</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373] mb-4">Contact</p>
            <ul className="space-y-2 text-sm text-[#525252]">
              <li>support@ordeneli.com</li>
              <li>London, United Kingdom</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#E5E7EB]">
          <div className="container-wide py-5 flex flex-col sm:flex-row justify-between gap-2 text-[10px] sm:text-xs text-[#737373] font-mono uppercase tracking-[0.15em] sm:tracking-[0.18em]">
            <span>© {new Date().getFullYear()} Ordeneli</span>
            <span>Built for marketers</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
